import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-manrope font-bold text-xl text-slate-900 mb-3">{title}</h2>
    <div className="text-sm text-slate-800 leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="tricolor-bar" />
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/" className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest">← Back to Home</Link>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl mt-4">Privacy Policy</h1>
          <p className="text-white/60 text-sm mt-3">weightloss-assessment.in · Last updated: 3 July 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Section title="1. Introduction">
          <p>
            This Privacy Policy explains how weightloss-assessment.in ("the Website", "we", "us")
            collects, uses, stores, and protects your personal and health information when you use
            our obesity risk assessment and Digital Twin simulation tools. We are committed to
            handling your information responsibly and in alignment with the Digital Personal Data
            Protection Act, 2023 (DPDP Act) and applicable Indian law.
          </p>
          <p>
            By using this Website and submitting your information, you consent to the practices
            described in this policy.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>When you use our assessment tools, we may collect the following information that you voluntarily provide:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Identity and contact details:</strong> name, mobile number, email address, and city.</li>
            <li><strong>Demographic details:</strong> age and gender.</li>
            <li><strong>Body measurements:</strong> height, weight, and calculated Body Mass Index (BMI).</li>
            <li><strong>Medical history:</strong> conditions you report, such as diabetes, hypertension, sleep apnea, acid reflux (GERD), osteoarthritis, and related symptoms.</li>
            <li><strong>Blood investigations and metabolic information:</strong> values you enter, such as HbA1c, liver enzymes (AST/ALT), lipid profile, platelets, and albumin.</li>
            <li><strong>Additional notes:</strong> any free-text information you choose to share.</li>
          </ul>
          <p>We do not knowingly collect information from children under 18 years of age.</p>
        </Section>

        <Section title="3. Why We Collect This Information">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To generate your personalised obesity risk assessment and Digital Twin simulation.</li>
            <li>To prepare and provide your downloadable health report.</li>
            <li>To contact you, at your request, regarding your assessment or a consultation.</li>
            <li>To maintain records of assessments performed through the Website.</li>
            <li>To improve the accuracy and quality of our educational tools.</li>
          </ul>
        </Section>

        <Section title="4. How Your Information Is Used and Stored">
          <p>
            Information you submit is transmitted over an encrypted (HTTPS) connection and stored in
            secure, access-controlled records maintained by our team. Your information is used only
            for the purposes listed above and is not sold or rented to third parties.
          </p>
          <p>
            AI-assisted tools may be used to generate personalised simulations and educational
            insights from the information you provide.
          </p>
        </Section>

        <Section title="5. Who Can Access Your Information">
          <p>
            Access to submitted information is restricted to authorised members of the clinical and
            administrative team responsible for operating this Website and responding to your
            requests. We do not share your personal or health information with advertisers or
            unrelated third parties.
          </p>
        </Section>

        <Section title="6. How Long We Keep Your Information">
          <p>
            We retain your information only for as long as it is needed for the purposes described
            in this policy, or until you ask us to delete it, whichever is earlier. Records that are
            no longer required are deleted or anonymised.
          </p>
        </Section>

        <Section title="7. Your Rights — Correction and Deletion">
          <p>
            You may request access to, correction of, or deletion of your personal information at
            any time. To make a request, please email{' '}
            <a href="mailto:contact@weightloss-assessment.in" className="text-indigo-600 font-semibold underline">
              contact@weightloss-assessment.in
            </a>{' '}
            with the name and phone number you used in your assessment. We will act on verified
            requests within a reasonable time.
          </p>
        </Section>

        <Section title="8. Data Security">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>All data is transmitted over HTTPS encryption.</li>
            <li>Stored records are access-controlled and limited to authorised team members.</li>
            <li>Administrative access is password-protected.</li>
            <li>Patient records are not published or made publicly accessible.</li>
          </ul>
        </Section>

        <Section title="9. Important Notice">
          <p>
            This Website provides educational assessments only. It does not provide medical
            diagnosis or treatment, and use of the Website does not create a doctor-patient
            relationship. Please see our{' '}
            <Link to="/terms" className="text-indigo-600 font-semibold underline">Terms of Use</Link>{' '}
            for full details.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            For any privacy-related questions or requests, contact:{' '}
            <a href="mailto:contact@weightloss-assessment.in" className="text-indigo-600 font-semibold underline">
              contact@weightloss-assessment.in
            </a>
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date at the top
            of this page reflects the most recent revision. Continued use of the Website after
            changes are posted constitutes acceptance of the revised policy.
          </p>
        </Section>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
